import { BROWSER } from 'esm-env';
import { datadogLogs, StatusType } from '@datadog/browser-logs';
import { stringify } from 'safe-stable-stringify';
import { err as serializeErr } from 'pino-std-serializers';
import getCurrentLine from 'get-current-line';
import { generateUUID } from './helpers.js';
let loggerOptions = null;
const ENVIROMENT_LOCAL = 'local';
const SESSION_STORE_KEY = '_dd_s';
export const SESSION_ID_HEADER_KEY = 'x-session-id';
export const CORRELATION_ID_HEADER_KEY = 'x-correlation-id';
const statusMap = {
    log: StatusType.info,
    info: StatusType.info,
    debug: StatusType.debug,
    error: StatusType.error,
    warn: StatusType.warn,
};
const colorMap = {
    log: '#00BCD4',
    info: '#f39c12',
    debug: '#f39c12',
    error: '#c0392b',
    warn: '#f8260e',
};
export const logger = {
    correlationId: undefined,
    log: (tag, ...message) => {
        handleLog(tag, message, 'log', logger.correlationId);
    },
    info: (tag, ...message) => {
        handleLog(tag, message, 'info', logger.correlationId);
    },
    debug: (tag, ...message) => {
        handleLog(tag, message, 'debug', logger.correlationId);
    },
    error: (tag, ...message) => {
        handleLog(tag, message, 'error', logger.correlationId);
    },
    warn: (tag, ...message) => {
        handleLog(tag, message, 'warn', logger.correlationId);
    },
};
export const getLoggerWithCorrelationId = (correlationId) => {
    const loggerWithCorrelationId = { ...logger };
    logger.correlationId = correlationId ?? generateUUID();
    return loggerWithCorrelationId;
};
function handleLog(tag, message, logType, correlationId) {
    const loggerOptions = getLoggerOptions();
    let location = undefined;
    try {
        const currentLine = getCurrentLine({ frames: 3 });
        location = `${new URL(currentLine.file).pathname}:${currentLine.line}`;
    }
    catch (e) { }
    if (BROWSER) {
        const style = `background: ${colorMap[logType]}; border-radius: 0.5em; color: white; font-weight: bold; padding: 2px 0.5em`;
        if (location) {
            // @ts-ignore
            console[logType](`%c%s`, style, tag, ...message, `  [${location}]`);
        }
        else {
            // @ts-ignore
            console[logType](`%c%s`, style, tag, ...message);
        }
        if (datadogLogs && datadogLogs.logger) {
            datadogLogs.logger.log([tag, ...message].join(' '), { tag, messageRaw: message, correlationId }, statusMap[logType]);
        }
    }
    else {
        if ((loggerOptions.environment !== ENVIROMENT_LOCAL || loggerOptions.forceLog4j) &&
            process.env.NODE_ENV !== 'development') {
            // log to json
            const result = {
                service: loggerOptions.service,
                environment: loggerOptions.environment,
                status: logType,
                tag,
                message: [tag, ...message].join(' '),
                messageRaw: message.map(item => serializeErr(item)),
                source: 'server',
                date: new Date().getTime(),
                version: loggerOptions.version,
                session_id: loggerOptions.sessionId,
                correlationId: correlationId ?? loggerOptions.correlationId,
            };
            // @ts-ignore
            console[logType](stringify(result));
            if (loggerOptions.platform?.context?.waitUntil && loggerOptions.datadogApiKey) {
                // @docs https://docs.datadoghq.com/api/latest/logs/
                // @docs https://developers.cloudflare.com/workers/runtime-apis/fetch-event/#waituntil
                loggerOptions.platform.context.waitUntil(fetch(`https://http-intake.logs.datadoghq.eu/api/v2/logs?ddsource=server&ddtags=env:${loggerOptions.environment},service:${loggerOptions.service},version:${loggerOptions.version},tag:${tag}`, {
                    method: 'POST',
                    body: stringify([{ ...result, cf: loggerOptions.platform?.cf }]),
                    headers: {
                        Accept: 'application/json',
                        'content-type': 'application/json;charset=UTF-8',
                        'DD-API-KEY': loggerOptions.datadogApiKey,
                    },
                }).catch(err => console.log(`Can't send log.`, err)));
            }
        }
        else {
            // pretty logging for local development
            // @ts-ignore
            console[logType](logType.toUpperCase(), `[${tag}]`, ...message);
        }
    }
}
function getLoggerOptions() {
    if (!loggerOptions) {
        initLogger({
            environment: 'local',
            service: 'unknown-service', // TODO: lze nějak určit service? například z package.json?
        });
    }
    return loggerOptions;
}
export function initLogger({ environment, service, version, cookies, platform, datadogApiKey, correlationId, forceLog4j = false, }) {
    loggerOptions = {
        environment,
        service: `${service}-${environment}`,
        version,
        platform,
        datadogApiKey: datadogApiKey,
        forceLog4j,
        sessionId: cookies ? getSessionIdFromCookie(cookies) : generateUUID(),
        correlationId: correlationId ?? (!BROWSER ? generateUUID() : null),
    };
    if (BROWSER && environment !== ENVIROMENT_LOCAL) {
        datadogLogs.init({
            clientToken: 'pub67d30575d0e24d3bfac98d22ca891648',
            site: 'datadoghq.eu',
            service: loggerOptions.service,
            env: environment,
            version,
            forwardErrorsToLogs: true,
            forwardConsoleLogs: [],
            sessionSampleRate: 100,
        });
    }
    if (BROWSER) {
        ;
        window.logger = logger;
    }
    else {
        ;
        globalThis.logger = logger;
    }
}
export function setSessionId(sessionId) {
    if (loggerOptions) {
        loggerOptions.sessionId = sessionId;
    }
}
export function getSessionId() {
    return loggerOptions?.sessionId;
}
export function getSessionIdFromCookie(cookies) {
    const session = cookies.get(SESSION_STORE_KEY);
    const sessionDecoded = new URLSearchParams(session);
    return sessionDecoded.get('id');
}
export function getCorrelationId() {
    return loggerOptions?.correlationId;
}
